import {
    Contact,
    CreateContactInput,
    CreateContactMutation,
    CreateContactMutationVariables,
    DeleteContactMutationVariables,
    GetContactQuery,
    GetContactQueryVariables,
    ListContactsQuery,
    ListContactsQueryVariables,
    UpdateContactInput,
    UpdateContactMutation,
    UpdateContactMutationVariables
} from "../../../API";
import GraphQLAPI, {
    GraphQLResult
} from "@aws-amplify/api-graphql";
import {
    createContact,
    deleteContact,
    updateContact
} from "../../../graphql/mutations";
import {
    getContact,
    listContacts
} from "../../../graphql/queries";

import { AuthIDTokenSupplierFactory } from "../../auth/AuthIDTokenSupplierFactory";
import ClientLogger from "../../logging/ClientLogger";
import ClientLoggerFactory from "../../logging/ClientLoggerFactory";
import { ContactDAO } from "./ContactDAO";
import { IDTokenSupplier } from "../../auth/IDTokenSupplier";

export class GraphQLContactDAO implements ContactDAO {
    private static readonly LIST_ATTEMPT_METRIC_NAME = "GraphQLContactDAO.List.Attempt";
    private static readonly LIST_FAILURE_METRIC_NAME = "GraphQLContactDAO.List.Failure";
    private static readonly GET_ATTEMPT_METRIC_NAME = "GraphQLContactDAO.Get.Attempt";
    private static readonly GET_FAILURE_METRIC_NAME = "GraphQLContactDAO.Get.Failure";
    private static readonly UPDATE_ATTEMPT_METRIC_NAME = "GraphQLContactDAO.Update.Attempt";
    private static readonly UPDATE_FAILURE_METRIC_NAME = "GraphQLContactDAO.Update.Failure";
    private static readonly DELETE_ATTEMPT_METRIC_NAME = "GraphQLContactDAO.Delete.Attempt";
    private static readonly DELETE_FAILURE_METRIC_NAME = "GraphQLContactDAO.Delete.Failure";
    private static readonly CREATE_ATTEMPT_METRIC_NAME = "GraphQLContactDAO.Create.Attempt";
    private static readonly CREATE_FAILURE_METRIC_NAME = "GraphQLContactDAO.Create.Failure";

    private readonly logger: ClientLogger;
    private readonly idTokenSupplier: IDTokenSupplier;

    constructor(
        logger: ClientLogger,
        idTokenSupplier: IDTokenSupplier
    ) {
        this.logger = logger;
        this.idTokenSupplier = idTokenSupplier;
    }

    public async getById(
        id: string
    ): Promise<Contact> {
        this.logger.info(
            `Getting Contact record with id=${id}`,
            { id },
            [GraphQLContactDAO.GET_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: GetContactQueryVariables = { id };
            const result: GraphQLResult<GetContactQuery> = await GraphQLAPI.graphql({
                query: getContact,
                variables,
                authToken
            }) as GraphQLResult<GetContactQuery>;
            if (!result.data?.getContact) {
                throw new Error(`No Contact found with id=${id}`);
            }
            return result.data.getContact as Contact;
        }
        catch (error) {
            const errorMessage = `Unable to get Contact record with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLContactDAO.GET_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage);
        }
    }

    public async list(
        nextToken?: string
    ): Promise<Array<Contact>> {
        this.logger.info(
            `Listing Contact records with nextToken=${nextToken}`,
            {},
            [GraphQLContactDAO.LIST_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: ListContactsQueryVariables = {
                filter: {
                    _deleted: { ne: true }
                },
                nextToken: nextToken
            };
            const result = await GraphQLAPI.graphql<ListContactsQuery>({
                query: listContacts,
                variables,
                authToken
            }) as GraphQLResult<ListContactsQuery>;
            return result.data?.listContacts?.items as Array<Contact> || [];
        } catch (error) {
            const errorMessage = `Unable to list Contact records with nextToken=${nextToken}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLContactDAO.LIST_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage);
        }
    }

    public async create(
        contact: CreateContactInput
    ): Promise<Contact> {
        this.logger.info(
            `Creating Contact record with input=${JSON.stringify(contact)}`,
            { contact },
            [GraphQLContactDAO.CREATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: CreateContactMutationVariables = { input: contact };
            const result: GraphQLResult<CreateContactMutation> = await GraphQLAPI.graphql({
                query: createContact,
                variables: variables,
                authToken
            }) as GraphQLResult<CreateContactMutation>;
            return result.data?.createContact as Contact;
        } catch (error) {
            const errorMessage = `Unable to create Contact record with input=${JSON.stringify(contact)}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLContactDAO.CREATE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage);
        }
    }

    public async update(
        contact: UpdateContactInput
    ): Promise<Contact> {
        this.logger.info(
            `Updating Contact record with id=${contact.id} and input=${JSON.stringify(contact)}`,
            { contact },
            [GraphQLContactDAO.UPDATE_ATTEMPT_METRIC_NAME]
        );
        try {
            const authToken: string = await this.idTokenSupplier.get();
            const variables: UpdateContactMutationVariables = { input: contact };
            const result: GraphQLResult<UpdateContactMutation> = await GraphQLAPI.graphql({
                query: updateContact,
                variables,
                authToken
            }) as GraphQLResult<UpdateContactMutation>;
            return result.data?.updateContact as Contact;
        } catch (error) {
            const errorMessage = `Unable to update Contact record with id=${contact.id} and input=${JSON.stringify(contact)}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLContactDAO.UPDATE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage);
        }
    }

    public async delete(
        id: string
    ): Promise<void> {
        this.logger.info(
            `Deleting Contact record with id=${id}`,
            { id },
            [GraphQLContactDAO.DELETE_ATTEMPT_METRIC_NAME]
        );
        try {
            const contact: Contact = await this.getById(id);
            const authToken: string = await this.idTokenSupplier.get();
            const variables: DeleteContactMutationVariables = {
                input: {
                    id,
                    _version: contact._version!
                }
            };
            await GraphQLAPI.graphql({
                query: deleteContact,
                variables,
                authToken
            });
        } catch (error) {
            const errorMessage = `Unable to delete Contact record with id=${id}`;
            this.logger.error(
                errorMessage,
                error,
                [GraphQLContactDAO.DELETE_FAILURE_METRIC_NAME]
            );
            throw new Error(errorMessage);
        }
    }
}

export const graphQLContactDAO = new GraphQLContactDAO(
    ClientLoggerFactory.getClientLogger("GraphQLContactDAO"),
    AuthIDTokenSupplierFactory.getInstance()
);